import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  MapPin,
  Phone,
  User,
  PlusCircle
} from 'lucide-react';

import ListOfferingsForSettings from '@/app/offering/ListOfferingsForSettings';
import {
  COACH_ACTIVITY_STATUS,
  CONTINUE_APPLICATION_BUTTON_TEXT
} from '@/app/coachActivity/enums';

import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

import StatusBadge from './components/coachSettings/StatusBadge';

import { useCoachActivities } from '@/data/coachActivity';
import { useCoachSelf } from '@/data/coach';

const CoachSettingsCard = () => {
  const coach = useCoachSelf();
  const coachActivities = useCoachActivities();

  const [expandedActivity, setExpandedActivity] = useState(null);

  if (coach.isLoading && !coach.data) {
    return null;
  }
  if (coachActivities.isLoading && !coachActivities.data) {
    return null;
  }

  const toggleActivity = (id) => {
    setExpandedActivity(expandedActivity === id ? null : id);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-2xl">Pro Settings</CardTitle>
        <CardDescription>
          Manage your ProFound Pro profile and activities
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Coach Info Section */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <Badge className="mb-2">Coach</Badge>
              <div className="flex items-center text-sm text-muted-foreground mt-2">
                <MapPin className="h-4 w-4 mr-1" />
                <span>{coach.data.coachRegions[0].region.name}</span>
              </div>
              <div className="flex items-center text-sm text-muted-foreground mt-1">
                <Phone className="h-4 w-4 mr-1" />
                <span>{coach.data.phone}</span>
              </div>
            </div>
            <Link to="/coach/profile">
              <Button variant="secondary" className="flex items-center">
                <User className="h-4 w-4 mr-2" />
                Edit Profile
              </Button>
            </Link>
          </div>
        </div>

        <Separator />

        {/* Activities Section */}
        <div>
          <h3 className="text-lg mb-4">You are a Pro for</h3>

          <div className="space-y-3">
            {coachActivities.data.map((coachActivity) => (
              <div
                key={coachActivity.id}
                className="border rounded-lg overflow-hidden"
              >
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleActivity(coachActivity.id)}
                >
                  <div className="flex items-center">
                    <h4 className="font-medium mr-3">
                      {coachActivity.activity.title}
                    </h4>
                    <StatusBadge status={coachActivity.status} />
                  </div>
                  <div>
                    {expandedActivity === coachActivity.id ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {expandedActivity === coachActivity.id && (
                  <div className="p-4 pt-0 border-t">
                    <div className="space-y-4">
                      {coachActivity.status ===
                        COACH_ACTIVITY_STATUS.REJECTED && (
                        <Alert variant="destructive" className="mt-4">
                          <AlertTitle>Rejected</AlertTitle>
                          <AlertDescription>
                            &quot;{coachActivity.rejectionMessage}&quot;
                          </AlertDescription>
                          <AlertDescription>
                            Please address any issues and resubmit your
                            application.
                          </AlertDescription>
                        </Alert>
                      )}
                      <ListOfferingsForSettings
                        coachActivityId={coachActivity.id}
                      />

                      <div className="flex justify-end">
                        <Link to={`/application/${coachActivity.id}`}>
                          <Button variant="outline" size="sm">
                            {
                              CONTINUE_APPLICATION_BUTTON_TEXT[
                                coachActivity.status
                              ]
                            }
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>

      <CardFooter>
        <Link to="/application/add" className="w-full">
          <Button className="w-full" variant="outline">
            <PlusCircle className="h-4 w-4 mr-2" />
            Apply to Coach New Activity
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
};

export default CoachSettingsCard;
